/**
 * Lenavio Connect — Slack-integration card.
 *
 * Three render branches:
 *   1. Plan-locked (no ``slack_integration`` feature) → upsell.
 *   2. Not connected             → "Connect to Slack" button.
 *   3. Connected (with workspace name + bot user) → "Disconnect".
 *
 * Mirrors the structure of ``TelegramBotCard`` so the two bots
 * present consistently in Settings → Integrations.
 */
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Check,
  Hash,
  Lock,
  MessageSquareText,
  Sparkles,
} from "lucide-react";

import {
  disconnectSlack,
  getSlackInstallUrl,
  getSlackStatus,
  setSlackChannel,
  type SlackStatusResponse,
} from "@/api/integrationsSlack";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { usePlanFeatures } from "@/hooks/usePlanFeatures";
import { useToast } from "@/hooks/useToast";

export function SlackCard() {
  const { features, has, loading: featuresLoading } = usePlanFeatures();
  const planLocked = features != null && !has("slack_integration");
  const { addToast } = useToast();
  const [params, setParams] = useSearchParams();

  const [status, setStatus] = useState<SlackStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [installing, setInstalling] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [channelDraft, setChannelDraft] = useState("");
  const [savingChannel, setSavingChannel] = useState(false);

  const refresh = useCallback(async () => {
    if (planLocked) {
      setStatus(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const s = await getSlackStatus();
      setStatus(s);
      setChannelDraft(s.notify_channel_id || "");
    } catch {
      // 402/403 land here when the plan-gate trips between client
      // and server — treat as "not connected" rather than crashing.
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, [planLocked]);

  useEffect(() => {
    if (featuresLoading) return;
    void refresh();
  }, [refresh, featuresLoading]);

  // Surface the OAuth-callback redirect-back state. The backend
  // appends ?slack_connected=1 on success and ?slack_error=… on
  // failure; clean the params after we've shown the toast.
  useEffect(() => {
    const ok = params.get("slack_connected");
    const err = params.get("slack_error");
    if (ok) {
      addToast("success", "Slack workspace connected!");
      params.delete("slack_connected");
      setParams(params, { replace: true });
      void refresh();
    } else if (err) {
      addToast("error", `Slack connection failed: ${err}`);
      params.delete("slack_error");
      setParams(params, { replace: true });
    }
    // refresh / params are stable enough; we only want this on mount-equivalent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConnect = async () => {
    setInstalling(true);
    try {
      const res = await getSlackInstallUrl();
      // Full-page redirect to Slack — same pattern as Google OAuth.
      // The state cookie set on this response has to ride along
      // through Slack and back, which only works on a real
      // navigation (not an XHR).
      window.location.href = res.authorize_url;
    } catch (e: unknown) {
      const detail =
        (e as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail || "Could not start Slack install.";
      addToast("error", detail);
      setInstalling(false);
    }
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    try {
      const res = await disconnectSlack();
      setStatus(res);
      addToast("success", "Slack disconnected.");
    } catch {
      addToast("error", "Could not disconnect Slack.");
    } finally {
      setDisconnecting(false);
    }
  };

  const handleSaveChannel = async () => {
    setSavingChannel(true);
    try {
      const res = await setSlackChannel(channelDraft.trim());
      setStatus(res);
      setChannelDraft(res.notify_channel_id || "");
      addToast(
        "success",
        res.notify_channel_id
          ? "Notification channel saved."
          : "Notification channel cleared.",
      );
    } catch (e: unknown) {
      const detail =
        (e as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail || "Could not save the channel.";
      addToast("error", detail);
    } finally {
      setSavingChannel(false);
    }
  };

  return (
    <Card className={planLocked ? "opacity-80" : undefined}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-accent/10 p-2">
            <MessageSquareText className="h-5 w-5 text-accent" aria-hidden />
          </div>
          <div>
            <h3 className="text-sm font-semibold font-heading text-text-primary">
              Slack
            </h3>
            <p className="text-[11px] uppercase tracking-wider text-text-muted">
              Communication
            </p>
          </div>
        </div>
        {!planLocked && status?.connected && <Badge variant="success">Connected</Badge>}
        {!planLocked && status && !status.connected && (
          <Badge variant="default">Not connected</Badge>
        )}
        {planLocked && (
          <Badge variant="default" className="uppercase tracking-wide">
            Pro
          </Badge>
        )}
      </div>

      {/* Plan-locked */}
      {planLocked && (
        <>
          <p className="text-sm text-text-muted mb-4">
            Let your team check PTO, file leave requests, and approve
            them — directly in Slack with slash commands.
          </p>
          <div className="rounded-lg border border-dashed border-border bg-background p-4 text-sm flex items-start gap-3">
            <Lock className="h-4 w-4 mt-0.5 text-text-muted shrink-0" aria-hidden />
            <div>
              <p className="text-text-primary font-medium">Upgrade to Professional</p>
              <p className="text-text-muted">
                The Slack integration is included on Professional and
                Enterprise plans (and during your trial).
              </p>
            </div>
          </div>
          <div className="mt-4">
            <a
              href="/settings/billing"
              className="inline-flex items-center gap-2 rounded-lg border border-accent bg-accent px-3 py-2 text-sm font-medium text-white hover:bg-accent/90"
            >
              <Sparkles className="h-4 w-4" />
              View plans
            </a>
          </div>
        </>
      )}

      {/* Loading */}
      {!planLocked && loading && (
        <div className="h-12 rounded bg-border/30 animate-pulse" />
      )}

      {/* Connected */}
      {!planLocked && !loading && status?.connected && (
        <>
          <p className="text-sm mb-2 text-text-primary">
            <Check
              className="inline h-4 w-4 -mt-0.5 mr-1 text-success"
              aria-hidden
            />
            Connected to <strong>{status.workspace_name || "Slack"}</strong>
          </p>

          <div className="rounded-lg border border-dashed border-border bg-background p-3 text-xs text-text-muted space-y-1.5 mb-4">
            <p className="text-text-primary font-medium">Slash commands</p>
            <p>
              Your team can now use these commands in any Slack channel
              they're invited to:
            </p>
            <ul className="space-y-0.5 ml-4">
              <li>
                <code className="bg-surface px-1.5 py-0.5 rounded">/lenavio</code>{" "}
                — ask anything (e.g.{" "}
                <span className="italic">how many employees</span>,{" "}
                <span className="italic">my PTO balance</span>,{" "}
                <span className="italic">pending approvals</span>)
              </li>
              <li>
                <code className="bg-surface px-1.5 py-0.5 rounded">/lenavio-pto</code>{" "}
                — check PTO balance
              </li>
              <li>
                <code className="bg-surface px-1.5 py-0.5 rounded">/lenavio-request</code>{" "}
                — request time off
              </li>
              <li>
                <code className="bg-surface px-1.5 py-0.5 rounded">/lenavio-directory</code>{" "}
                — search the directory
              </li>
              <li>
                <code className="bg-surface px-1.5 py-0.5 rounded">/lenavio-approve</code>{" "}
                — approve pending requests (managers)
              </li>
              <li>
                <code className="bg-surface px-1.5 py-0.5 rounded">/lenavio-status</code>{" "}
                — your pending requests
              </li>
            </ul>
            <p className="pt-1">
              Lenavio auto-links each Slack user to their employee
              profile by matching their workspace email — no manual
              setup per person.
            </p>
          </div>

          {/* Outbound notification channel */}
          <div className="rounded-lg border border-border bg-surface p-3 mb-4">
            <p className="text-sm text-text-primary font-medium mb-1">
              Notification channel
            </p>
            <p className="text-xs text-text-muted mb-2">
              Lenavio posts events here — new hires, leave decisions,
              signed documents, announcements, and Atlas / Sentinel
              alerts. Invite the bot first with{" "}
              <code className="bg-background px-1 py-0.5 rounded">
                /invite @Lenavio
              </code>{" "}
              in the channel, then paste its channel ID (e.g.{" "}
              <code className="bg-background px-1 py-0.5 rounded">C01ABCD2EF</code>
              ).
            </p>
            <div className="flex items-center gap-2">
              <Input
                value={channelDraft}
                onChange={(e) => setChannelDraft(e.target.value)}
                placeholder="Channel ID (e.g. C01ABCD2EF)"
                className="flex-1"
              />
              <Button
                size="sm"
                onClick={handleSaveChannel}
                isLoading={savingChannel}
                disabled={channelDraft.trim() === (status.notify_channel_id || "")}
              >
                Save
              </Button>
            </div>
            {status.notify_channel_id ? (
              <p className="text-[11px] text-success mt-1.5">
                <Check className="inline h-3 w-3 -mt-0.5 mr-0.5" aria-hidden />
                Broadcasting to {status.notify_channel_id}
              </p>
            ) : (
              <p className="text-[11px] text-text-muted mt-1.5">
                No channel set — outbound notifications are paused.
              </p>
            )}
          </div>

          <Button
            size="sm"
            variant="ghost"
            onClick={handleDisconnect}
            isLoading={disconnecting}
          >
            Disconnect
          </Button>
        </>
      )}

      {/* Not connected */}
      {!planLocked && !loading && status && !status.connected && (
        <>
          <p className="text-sm text-text-muted mb-4">
            Connect Lenavio to your Slack workspace so your team can
            check PTO, file leave requests, search the directory,
            and approve manager requests — all without leaving Slack.
          </p>
          <Button onClick={handleConnect} isLoading={installing}>
            <Hash className="h-4 w-4" />
            Connect to Slack
          </Button>
        </>
      )}
    </Card>
  );
}
